import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { Plus } from 'lucide-react';

import { TextField, toOptions } from '~/components/form';
import { RecordPageShell } from '~/components/layout';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardSection,
  FormActions,
} from '~/components/ui';
import {
  COMPLIANCE_RULE_KEYS,
  COMPLIANCE_RULE_LABELS,
  FEE_BASES,
  FEE_CYCLES,
  MLS_DATA_TYPES,
  MLS_PROTOCOLS,
  MLS_VENDORS,
} from '~/components/Constants/mlsAgreementsConstants';
import { CredentialsPanel } from '~/components/pages/mls-agreements/CredentialsPanel';
import { DocumentsPanel } from '~/components/pages/mls-agreements/DocumentsPanel';
import { HistoryPanel } from '~/components/pages/mls-agreements/HistoryPanel';
import { MembershipList } from '~/components/pages/mls-agreements/MembershipList';
import { isNotFound } from '~/components/pages/mls-agreements/errors';
import {
  fieldId,
  RecordSections,
  SaveError,
  type SectionSpec,
} from '~/components/pages/mls-agreements/fields';
import type { MlsRecord, Scalar } from '~/components/pages/mls-agreements/types';
import { routes } from '~/app/routes';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { errorText } from '~/lib/apiError';
import { queryErrorMessage } from '~/modules/query';
import {
  useCreateAgreementMlsMutation,
  useGetAgreementMlsQuery,
  useResyncAgreementMlsMutation,
  useUpdateAgreementComplianceMutation,
  useUpdateAgreementMlsMutation,
} from '~/modules/query/mlsAgreements';

const SYNC_COLOR = { ok: 'success', pending: 'warning', failed: 'error' } as const;

type Draft = {
  name: Scalar;
  feedId: Scalar;
  status: Scalar;
  region: Scalar;
  dataType: Scalar;
  protocol: Scalar;
  vendor: Scalar;
  endpoint: Scalar;
  associationName: Scalar;
  vendorPaperwork: boolean;
  notes: Scalar;
  feeBasis: Scalar;
  feeAmount: Scalar;
  feeCycle: Scalar;
  feeInvoicedBy: Scalar;
  perAgentFeeAmount: Scalar;
  perAgentFeeCycle: Scalar;
  agentCapacity: Scalar;
};
type Rules = Record<string, unknown>;

// A new MLS seeds from an empty record, so the defaults live in one place: `toDraft`.
const NEW: MlsRecord = {};
const NO_RULES: Rules = {};

const toDraft = (res: MlsRecord): Draft => ({
  name: res.name ?? '',
  feedId: res.feedId ?? '',
  status: res.status ?? '',
  region: res.region ?? '',
  dataType: res.dataType ?? MLS_DATA_TYPES[0],
  protocol: res.protocol ?? MLS_PROTOCOLS[0],
  vendor: res.vendor ?? MLS_VENDORS[0],
  endpoint: res.endpoint ?? '',
  associationName: res.associationName ?? '',
  vendorPaperwork: !!res.vendorPaperwork,
  notes: res.notes ?? '',
  feeBasis: res.feeBasis ?? FEE_BASES[0],
  feeAmount: res.feeAmount ?? '',
  feeCycle: res.feeCycle ?? FEE_CYCLES[0],
  feeInvoicedBy: res.feeInvoicedBy ?? '',
  perAgentFeeAmount: res.perAgentFeeAmount ?? '',
  perAgentFeeCycle: res.perAgentFeeCycle ?? FEE_CYCLES[0],
  agentCapacity: res.agentCapacity ?? '',
});

const SECTIONS: SectionSpec<Draft>[] = [
  {
    title: 'General',
    fields: [
      { key: 'name', label: 'Name', required: true },
      { key: 'feedId', label: 'Feed ID', helper: 'Links to Configs.mls; blank = unlinked' },
      { key: 'status', label: 'Status' },
      { key: 'region', label: 'Region' },
      { key: 'dataType', label: 'Data type', kind: 'select', options: toOptions(MLS_DATA_TYPES) },
      { key: 'protocol', label: 'Protocol', kind: 'select', options: toOptions(MLS_PROTOCOLS) },
      { key: 'vendor', label: 'Vendor', kind: 'select', options: toOptions(MLS_VENDORS) },
      { key: 'endpoint', label: 'Endpoint' },
      { key: 'associationName', label: 'Association name' },
      {
        key: 'vendorPaperwork',
        label: 'Vendor paperwork',
        kind: 'toggle',
        text: 'Vendor handles paperwork',
      },
      { key: 'notes', label: 'Notes', kind: 'textarea', wide: true },
    ],
  },
  {
    title: 'Fee',
    columns: 3,
    fields: [
      {
        key: 'feeBasis',
        label: 'Basis',
        kind: 'select',
        options: toOptions(FEE_BASES, (v) => (v === 'per_agent' ? 'Per agent' : 'Vendor')),
      },
      { key: 'feeAmount', label: 'Amount', kind: 'number' },
      { key: 'feeCycle', label: 'Cycle', kind: 'select', options: toOptions(FEE_CYCLES) },
      { key: 'feeInvoicedBy', label: 'Invoiced by' },
      {
        key: 'perAgentFeeAmount',
        label: 'Per-agent amount',
        kind: 'number',
        show: (d) => d.feeBasis === 'per_agent',
      },
      {
        key: 'perAgentFeeCycle',
        label: 'Per-agent cycle',
        kind: 'select',
        options: toOptions(FEE_CYCLES),
        show: (d) => d.feeBasis === 'per_agent',
      },
      {
        key: 'agentCapacity',
        label: 'Agents covered',
        kind: 'number',
        helper: 'How many agents this agreement covers',
      },
    ],
  },
];

const schema = z.object({ name: z.string().trim().min(1, 'Enter the MLS name.') });

const FORM_ID = 'mls-form';
const RULES_FORM_ID = 'mls-compliance-form';

/** Create (`/new`) and edit (`/:id`) one MLS agreement record (DESIGN.md §1.6). */
export default function MlsPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const mlsId = id ?? '';
  const isNew = !id;

  const query = useGetAgreementMlsQuery(mlsId, { skip: isNew });
  const record = query.currentData as MlsRecord | undefined;
  const { draft, patch, dirty, reset, markSaved } = useRecordDraft<MlsRecord, Draft>(
    isNew ? NEW : record,
    { id: id ?? 'new', version: record?.updatedAt, toDraft },
  );
  const rulesRecord = record && (record.complianceRules ?? NO_RULES);
  const rules = useRecordDraft<Rules, Rules>(rulesRecord, {
    id: id ?? 'new',
    version: JSON.stringify(rulesRecord ?? null),
    toDraft: (r) => ({ ...r }),
  });
  const guard = useUnsavedGuard(dirty || rules.dirty);
  const v = useFormValidation(schema, draft, (field) => fieldId('mls', field));

  const [createMls] = useCreateAgreementMlsMutation();
  const [updateMls] = useUpdateAgreementMlsMutation();
  const [updateCompliance] = useUpdateAgreementComplianceMutation();
  const [resyncMls] = useResyncAgreementMlsMutation();
  const createdId = useRef<string | undefined>(undefined);

  const save = useRecordSave<Draft>({
    save: async (d) => {
      if (isNew) createdId.current = (await createMls(d).unwrap()).id;
      else await updateMls({ id: mlsId, data: { ...d, updatedAt: record?.updatedAt } }).unwrap();
    },
    success: isNew ? 'MLS created' : 'MLS saved',
    failure: 'The MLS could not be saved. Try again.',
  });
  const saveRules = useRecordSave<Rules>({
    save: (r) => updateCompliance({ id: mlsId, rules: r }).unwrap(),
    success: 'Compliance rules saved',
    failure: 'The compliance rules could not be saved. Try again.',
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft || !v.submit()) return;
    if (!(await save.submit(draft))) return;
    if (!isNew) return markSaved(draft);
    guard.release();
    navigate(
      createdId.current ? routes.mlsAgreementsMls(createdId.current) : routes.mlsAgreements,
      {
        replace: true,
      },
    );
  };

  const resync = async () => {
    try {
      await resyncMls(mlsId).unwrap();
      toast.success('Resync started');
    } catch (err) {
      toast.error(errorText(err, 'The resync could not be started. Try again.'));
    }
  };

  const title = isNew ? 'Create MLS' : record?.name || 'MLS';
  const notFound = !isNew && isNotFound(query.error);
  const failed = query.isError && !notFound;

  const sync = record && (
    <CardSection title="Sync">
      <div className="flex flex-wrap items-center gap-2">
        <Badge size="sm" color={SYNC_COLOR[record.syncStatus as keyof typeof SYNC_COLOR] ?? 'gray'}>
          {record.syncStatus ?? '—'}
        </Badge>
        {record.syncStatus === 'failed' && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {record.syncAttempts} attempts — {record.syncError}
          </span>
        )}
        {record.feedId && (
          <Button size="sm" variant="outline" onClick={resync} testId="mls-resync">
            Resync
          </Button>
        )}
      </div>
    </CardSection>
  );

  return (
    <RecordPageShell
      title={title}
      breadcrumbs={[{ label: 'MLS agreements', href: routes.mlsAgreements }, { label: title }]}
      loading={!draft && !query.isError}
      notFound={notFound}
      notFoundTitle="MLS not found"
      backTo={routes.mlsAgreements}
      backLabel="Back to MLS agreements"
      error={failed ? { what: 'the MLS', message: queryErrorMessage(query.error) } : null}
      onRetry={() => query.refetch()}
      guard={guard}
      testId="mls"
    >
      {draft && (
        <>
          <form id={FORM_ID} noValidate onSubmit={submit}>
            <Card>
              <CardBody className="space-y-6">
                <SaveError message={save.error} />
                {sync}
                <RecordSections
                  prefix="mls"
                  sections={SECTIONS}
                  draft={draft}
                  patch={patch}
                  errors={v.errors}
                  touch={v.touch}
                />
              </CardBody>
              <CardFooter>
                <FormActions
                  form={FORM_ID}
                  {...(isNew
                    ? {
                        discardHref: routes.mlsAgreements,
                        discardLabel: 'Cancel',
                        saveLabel: 'Create MLS',
                        busyLabel: 'Creating…',
                      }
                    : {
                        onDiscard: () => {
                          reset();
                          v.reset();
                          save.clearError();
                        },
                        disabled: !dirty,
                        disabledReason: 'No changes to save',
                      })}
                  busy={save.busy}
                  saveTestId="mls-save"
                />
              </CardFooter>
            </Card>
          </form>

          {!isNew && rules.draft && (
            <Card>
              <CardBody className="space-y-6">
                <CardSection title="Compliance">
                  <SaveError message={saveRules.error} />
                  <form
                    id={RULES_FORM_ID}
                    className="space-y-4"
                    onSubmit={async (event) => {
                      event.preventDefault();
                      const sent = rules.draft ?? {};
                      if (await saveRules.submit(sent)) rules.markSaved(sent);
                    }}
                  >
                    {COMPLIANCE_RULE_KEYS.map((key) => (
                      <TextField
                        key={key}
                        id={`mls-rule-${key}`}
                        testId={`mls-rule-${key}`}
                        label={COMPLIANCE_RULE_LABELS[key] ?? key}
                        value={String(rules.draft?.[key] ?? '')}
                        onChange={(value) => rules.patch({ [key]: value })}
                      />
                    ))}
                    <FormActions
                      form={RULES_FORM_ID}
                      onDiscard={() => {
                        rules.reset();
                        saveRules.clearError();
                      }}
                      busy={saveRules.busy}
                      disabled={!rules.dirty}
                      disabledReason="No changes to save"
                      saveTestId="mls-save-rules"
                    />
                  </form>
                </CardSection>

                <CardSection
                  title="Memberships"
                  actions={
                    <Button
                      size="sm"
                      startIcon={<Plus className="size-4" aria-hidden="true" />}
                      href={`${routes.mlsAgreementsMembershipNew}?mlsId=${encodeURIComponent(mlsId)}`}
                      testId="mls-new-membership"
                    >
                      Create membership
                    </Button>
                  }
                >
                  <MembershipList
                    memberships={record?.memberships}
                    nameHeader="Customer"
                    nameOf={(m) => m.customerName ?? `Customer #${m.customerId}`}
                  />
                </CardSection>

                <CardSection title="Documents">
                  <DocumentsPanel
                    entityType="mls"
                    entityId={mlsId}
                    documents={record?.documents ?? []}
                  />
                </CardSection>

                <CardSection title="Credentials">
                  <CredentialsPanel mlsId={mlsId} />
                </CardSection>

                <CardSection title="History">
                  <HistoryPanel entityType="mls" entityId={mlsId} />
                </CardSection>
              </CardBody>
            </Card>
          )}
        </>
      )}
    </RecordPageShell>
  );
}
